import React from 'react';
import {Chip, Stack, Typography} from "@mui/material";
import {highlightText} from '../highlightText';
import {highlightEntitiesAndQuery, highlightMarkedSpansAndQuery} from '../highlightEntities';
import TagList from '../documentContent/TagList';
import SuggestedTags from '../documentContent/SuggestedTags';
import {useSegmentEntities} from '../documentContent/useSegmentEntities';
import {getClassificationByValue} from '../../features/products/reportEditorPage/components/addNewSource/constants';
import {apiSearchUnifiedTags} from '../../utils/api/tags';
import {getProvenanceFromDocSourceType} from '../../features/products/reportEditorPage/leftPanel/components/sourcesProvenance';
import {productTypeBadgeLabel} from '../../features/products/productTypes';
import SourceProvenanceChip from './SourceProvenanceChip';
import {formatDate} from '../../utils/formatTimestamp';

/**
 * SegmentContent - Displays the main content and metadata of a segment
 */
export default function SegmentContent({
                                           content,
                                           entities,
                                           docTitle,
                                           docSource,
                                           docSourceType,
                                           classificationLevel,
                                           segmentType,
                                           query,
                                           provenance,
                                           documentDate,
                                           productType,
                                           isReference,
                                           refNumber,
                                           isDisabled,
                                           handleRemoveReference,
                                           handleOpenSegment,
                                           openHandledByParent = false,
                                           // Tags
                                           segmentTagsLocal,
                                           createTag,
                                           voteTag,
                                           isPosting,
                                           loadingTagOps,
                                           docId,
                                           suggestedExistingTags,
                                           suggestedNewTags,
                                           segmentId,
                                           // My Data sentence-segment id — routes SuggestedTags to the DiTag suggest endpoint
                                           dataItemSegmentId,
                                           // For comments
                                           index,
                                           offset,
                                           start,
                                           end,
                                           compact = false
                                       }) {
    const classLevel = getClassificationByValue(classificationLevel);

    // Store-relative date from the federated search payload (corpus publish date, data-item
    // created date, report approval/updated date). Render a short, locale date when parseable;
    // hide the chip entirely for non-search cards or unparseable/absent values.
    const formattedDate = React.useMemo(() => {
        if (segmentType !== 'search' || !documentDate) return null;
        return formatDate(documentDate, null);
    }, [segmentType, documentDate]);

    const productTypeLabel = React.useMemo(
        () => productTypeBadgeLabel(productType),
        [productType],
    );

    // Federated search results render the shared SegmentItem card directly (no DocumentContent
    // wrapper), so they no longer inherit DocumentContent's per-segment entity fetch. The unified
    // search payload also doesn't carry entities. Restore entity/mention highlighting by lazily
    // fetching segment entity mappings for corpus search hits (numeric segmentId); the hook no-ops
    // for non-corpus hits (segmentId null) and when not in search mode. Inline `entities` (when a
    // caller already supplies them) take precedence.
    const hasInlineEntities = Array.isArray(entities) && entities.length > 0;
    const shouldFetchEntities = segmentType === 'search' && !hasInlineEntities;
    const {entities: fetchedEntities} = useSegmentEntities(shouldFetchEntities ? segmentId : null);
    const effectiveEntities = hasInlineEntities
        ? entities
        : (Array.isArray(fetchedEntities) ? fetchedEntities : []);

    // All stores use the unified /api/search/tags autocomplete so that any tag visible in the
    // filter picker can also be typed in the add-tag input on a result card. The per-store
    // create handler (useSearchResultTags → useDataItemTags / useDataItemSegmentTags / etc.)
    // still writes to the correct catalog table via find-or-create.
    const unifiedTagSearchFn = React.useMemo(
        () => (query) => apiSearchUnifiedTags(query),
        [],
    );

    // Federated search cards attach the open handler on the parent SegmentItem wrapper so
    // padding/metadata areas are clickable; interactive children stopPropagation.
    const stackOnClick = openHandledByParent ? undefined : handleOpenSegment;

    if (compact) {
        return (
            <Stack onClick={handleOpenSegment} spacing={0.5}>
                <Typography
                    variant="body2"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {docTitle || `Doc ${docId || segmentId}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Segment {segmentId}
                </Typography>
            </Stack>
        );
    }

    return (
        <>
            <Stack
                onClick={stackOnClick}
                spacing={1}>

                {isReference && typeof refNumber === 'number' && Number.isFinite(refNumber) && (
                    <Stack direction={'row'} align={'center'} justifyContent={'space-between'}>
                        <Typography variant="body2" color="text.secondary">
                            [{refNumber}]
                        </Typography>

                        <Typography
                            variant="body2"
                            color="error"
                            sx={{
                                display: 'inline',
                                cursor: isDisabled ? 'default' : 'pointer',
                                textDecoration: isDisabled ? 'none' : 'underline',
                                opacity: isDisabled ? 0.5 : 1,
                                userSelect: 'none'
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isDisabled) handleRemoveReference();
                            }}
                            role="button"
                            aria-disabled={isDisabled}
                        >
                            Remove
                        </Typography>
                    </Stack>
                )}

                <Typography>
                    {segmentType === 'search'
                        ? (docSourceType === 'HIGHLIGHT'
                            // Highlight hits: the span marks the user's own saved excerpt within its
                            // context, not a recognized entity — render as a yellow highlighter mark
                            // rather than the wikidata-entity (linked/mention) convention.
                            ? highlightMarkedSpansAndQuery(content ?? '', effectiveEntities ?? [], query, highlightText)
                            : highlightEntitiesAndQuery(content ?? '', effectiveEntities ?? [], query, highlightText))
                        : content}
                </Typography>
            </Stack>

            {!isReference && (
                <TagList
                    tags={segmentTagsLocal}
                    createTag={createTag}
                    voteTag={voteTag}
                    isPosting={isPosting}
                    loadingTagOps={loadingTagOps}
                    docId={docId}
                    searchTagsFn={unifiedTagSearchFn}
                />
            )}

            {!isReference && (
                <SuggestedTags
                    segmentId={segmentId}
                    dataItemSegmentId={dataItemSegmentId}
                    contentText={content}
                    existingTags={suggestedExistingTags}
                    newTags={suggestedNewTags}
                    onAddExisting={(name) => voteTag(name)}
                    onAddNew={(name) => createTag(name)}
                    disabled={isPosting}
                    currentTags={segmentTagsLocal}
                />
            )}

            {segmentType === 'search' ? (
                <Stack spacing={0.25} sx={{mt: 0.5}}>
                    <Typography
                        variant={'overline'}
                        color={'text.secondary'}
                        sx={{lineHeight: 1.2, letterSpacing: 0.6, fontSize: '0.65rem'}}
                    >
                        Document title
                    </Typography>
                    <Typography
                        variant={'subtitle2'}
                        color={'text.primary'}
                        sx={{fontWeight: 600, lineHeight: 1.3}}
                    >
                        {highlightText(docTitle ?? '', query)}
                    </Typography>
                </Stack>
            ) : (
                <Typography
                    variant={'body2'}
                    color={'text.secondary'}
                    style={{textDecoration: 'none'}}
                >
                    {docTitle}
                </Typography>
            )}

            {segmentType === 'reference' && (
                <Typography variant={'body2'} color={'text.secondary'}>{docSource}</Typography>
            )}

            {segmentType !== 'reference' && (
                <Stack direction={'row'} flexWrap={'wrap'} gap={1} alignItems={'center'}>
                    {provenance && <SourceProvenanceChip provenance={provenance}/>}

                    {/* FINTEL / IIR / Pitch / … next to the Products provenance badge */}
                    {productTypeLabel && (
                        <Chip
                            size="small"
                            label={productTypeLabel}
                            variant="outlined"
                            color="warning"
                            title="Product type"
                        />
                    )}

                    <Typography
                        variant={'body2'}
                        color={'text.primary'}
                        bgcolor={classLevel.bgColor}
                    >{classLevel.label}</Typography>

                    {/* Source indicator: the provenance chip is the canonical source badge for
                        federated search hits (Corpus / My Data / Reports / Uploaded by …). Saved
                        Source Package items only carry the raw docSourceType sentinel (the full
                        ownership object doesn't survive drag-and-drop), so derive the same badge
                        from that instead of falling back to plain, uncolored enum text — every
                        "source" badge in the Source Package UI renders identically either way. */}
                    {!provenance && docSourceType && (
                        <SourceProvenanceChip provenance={getProvenanceFromDocSourceType(docSourceType)}/>
                    )}

                    <Typography
                        variant={'body2'}
                    >{docSource ? docSource : 'domain.com'}</Typography>

                    {formattedDate && (
                        <Typography
                            variant={'body2'}
                            color={'text.secondary'}
                            title={'Document date'}
                            sx={{ml: 'auto', whiteSpace: 'nowrap'}}
                        >{formattedDate}</Typography>
                    )}
                </Stack>
            )}
        </>
    );
}

